import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  AsyncClientSelect,
  StaticFilterSelect,
} from "components/Common/AsyncSearchSelects";
import {
  applyClientAsyncFilterOptionToFormState,
  buildClientAsyncFilterInitialOption,
  getClientAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import { fetchCommonJobOptions } from "model/JobModel";

const bulkTransferSchema = Yup.object().shape({
  clientId: Yup.string().trim().required("Client is required"),
  jobId: Yup.string().trim().required("Job is required"),
});

const mapJobRecordsToOptions = (response) => {
  const jobs =
    response?.data?.records ??
    response?.data?.data ??
    response?.data ??
    response?.records ??
    [];
  return (Array.isArray(jobs) ? jobs : [])
    .map((item) => {
      const value = String(
        item?.id ?? item?._id ?? item?.value ?? item?.jobId ?? ""
      ).trim();
      const label = String(
        item?.display ??
          item?.name ??
          item?.title ??
          item?.jobName ??
          item?.label ??
          ""
      ).trim();
      if (!value) return null;
      return { value, label: label || value };
    })
    .filter(Boolean);
};

const CandidateBulkTransferModal = ({
  isOpen,
  onClose,
  candidateIds = [],
  selectedCandidates = [],
  onSubmit,
  pageName = "Candidates",
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    clientId: "",
    [getClientAsyncFilterLabelFieldKey()]: "",
    jobId: "",
  });
  const [errors, setErrors] = useState({});
  const [jobOptions, setJobOptions] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  const selectedCandidateList = useMemo(() => {
    const fromProps = (Array.isArray(selectedCandidates) ? selectedCandidates : [])
      .map((candidate) => {
        const id = String(candidate?.id ?? "").trim();
        const name = String(candidate?.name ?? "").trim();
        if (!id) return null;
        return { id, name: name || id };
      })
      .filter(Boolean);

    if (fromProps.length) return fromProps;

    return (Array.isArray(candidateIds) ? candidateIds : [])
      .map((id) => {
        const normalizedId = String(id ?? "").trim();
        return normalizedId ? { id: normalizedId, name: normalizedId } : null;
      })
      .filter(Boolean);
  }, [selectedCandidates, candidateIds]);

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      clientId: "",
      [getClientAsyncFilterLabelFieldKey()]: "",
      jobId: "",
    });
    setErrors({});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const clientId = String(form?.clientId ?? "").trim();
    if (!clientId) {
      setJobOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setJobsLoading(true);
      try {
        const response = await fetchCommonJobOptions({ clientId });
        if (!cancelled) setJobOptions(mapJobRecordsToOptions(response));
      } finally {
        if (!cancelled) setJobsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, form.clientId]);

  const handleClientChange = (selectedOption) => {
    setForm((previousForm) => ({
      ...applyClientAsyncFilterOptionToFormState(previousForm, selectedOption),
      jobId: "",
    }));
    setJobOptions([]);
    if (selectedOption?.value) {
      setErrors((prev) => ({ ...prev, clientId: "" }));
    }
  };

  const handleJobChange = (selectedOption) => {
    setForm((previousForm) => ({
      ...previousForm,
      jobId: selectedOption?.value ?? "",
    }));
    if (selectedOption?.value) {
      setErrors((prev) => ({ ...prev, jobId: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ids = (Array.isArray(candidateIds) ? candidateIds : [])
      .map((id) => String(id ?? "").trim())
      .filter(Boolean);
    if (!ids.length) {
      toast.error("No candidates selected");
      return;
    }

    const clientId = String(form?.clientId ?? "").trim();
    const jobId = String(form?.jobId ?? "").trim();

    try {
      await bulkTransferSchema.validate({ clientId, jobId }, { abortEarly: false });
      setErrors({});
    } catch (validationError) {
      const validationErrors = {};
      validationError?.inner?.forEach((errorItem) => {
        if (errorItem?.path && !validationErrors[errorItem.path]) {
          validationErrors[errorItem.path] = errorItem.message;
        }
      });
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      const didSubmit = await onSubmit?.({ ids, clientId, jobId });
      if (didSubmit) {
        onClose?.();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    if (!submitting) onClose?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={closeModal}
      backdrop="static"
      size="md"
      className="modal-full-height"
      centered
    >
      <Form onSubmit={handleSubmit}>
        <ModalBody className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold mb-0">Bulk Candidate Transfer</h5>
            <Button close type="button" onClick={closeModal} disabled={submitting} />
          </div>
          <hr />

          {selectedCandidateList.length ? (
            <FormGroup>
              <Label className="mb-1">
                Selected candidates ({selectedCandidateList.length})
              </Label>
              <div className="d-flex flex-wrap gap-1">
                {selectedCandidateList.map((candidate) => (
                  <span
                    key={candidate.id}
                    className="badge bg-light text-dark border fw-normal"
                  >
                    {candidate.name}
                  </span>
                ))}
              </div>
            </FormGroup>
          ) : null}

          <FormGroup>
            <Label>
              Client <span className="text-danger">*</span>
            </Label>
            <div className={errors.clientId ? "is-invalid" : ""}>
              <AsyncClientSelect
                key={`bulk-transfer-client-${form.clientId}-${isOpen}`}
                pageName="CandidateFormRestrict"
                value={String(form?.clientId || "").trim()}
                initialOption={buildClientAsyncFilterInitialOption(
                  form.clientId,
                  form[getClientAsyncFilterLabelFieldKey()]
                )}
                onChange={handleClientChange}
                placeholder="Select Client"
                isClearable
              />
            </div>
            {errors.clientId ? (
              <div className="invalid-feedback d-block">{errors.clientId}</div>
            ) : null}
          </FormGroup>

          <FormGroup>
            <Label>
              Job <span className="text-danger">*</span>
            </Label>
            <div className={errors.jobId ? "is-invalid" : ""}>
              <StaticFilterSelect
                options={jobOptions}
                value={form.jobId}
                onChange={handleJobChange}
                placeholder={jobsLoading ? "Loading jobs..." : "Select Job"}
                isDisabled={jobsLoading}
              />
            </div>
            {errors.jobId ? (
              <div className="invalid-feedback d-block">{errors.jobId}</div>
            ) : null}
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={closeModal} disabled={submitting}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-1" /> Submitting…
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CandidateBulkTransferModal;
